import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserIcon,
  StarIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  MoonIcon,
  DevicePhoneMobileIcon,
  PhoneIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import MobileLayout from '../components/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { InstallSettings } from '../components/PWA';
import UsageDashboard from '../components/UsageDashboard';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  username: string;
  emailVerified: boolean;
  subscriptionTier: string;
  tokenBalance: number;
  createdAt: string;
  isTestUser: boolean;
  theme?: string;
  language?: string;
  emailNotifications?: boolean;
  marketingEmails?: boolean;
  pushNotifications?: boolean;
  phoneNumber?: string;
  phoneVerified?: boolean;
  smsConsentGiven?: boolean;
}

interface SettingsSection {
  icon: React.ElementType;
  title: string;
  items: {
    label: string;
    value?: string;
    action?: () => void;
    showChevron?: boolean;
    color?: string;
  }[];
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenBalance, setTokenBalance] = useState(0);

  useEffect(() => {
    fetchProfile();
    fetchTokenBalance();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/users/profile');
      setProfile(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load profile:', err);
      setLoading(false);
    }
  };

  const getLanguageDisplay = () => {
    const languageMap: Record<string, string> = {
      en: t('languages.english', 'English') + ' 🇺🇸',
      es: t('languages.spanish', 'Español') + ' 🇪🇸',
      fr: t('languages.french', 'Français') + ' 🇫🇷',
      de: t('languages.german', 'Deutsch') + ' 🇩🇪',
      it: t('languages.italian', 'Italiano') + ' 🇮🇹',
      pt: t('languages.portuguese', 'Português') + ' 🇵🇹',
      ru: t('languages.russian', 'Русский') + ' 🇷🇺',
      ja: t('languages.japanese', '日本語') + ' 🇯🇵',
      ko: t('languages.korean', '한국어') + ' 🇰🇷',
      zh: t('languages.chinese', '中文') + ' 🇨🇳',
    };
    return languageMap[profile?.language || 'en'] || t('languages.english', 'English') + ' 🇺🇸';
  };

  const fetchTokenBalance = async () => {
    try {
      const response = await axios.get('/tokens/balance');
      setTokenBalance(Number(response.data.currentBalance) || 0);
    } catch (error) {
      console.error('Failed to fetch token balance:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    // Show delete account confirmation modal
    if (
      !window.confirm(
        t(
          'profile.deleteAccountConfirm',
          'Are you sure you want to delete your account? This action cannot be undone.',
        ),
      )
    ) {
      return;
    }

    const password = window.prompt(
      t('profile.enterPasswordToDelete', 'Please enter your password to confirm account deletion:'),
    );
    if (!password) return;

    try {
      await axios.delete('/users/account', {
        data: { password },
      });
      await logout();
      navigate('/');
    } catch (error: any) {
      alert(
        error.response?.data?.message ||
          t('profile.deleteAccountError', 'Failed to delete account. Please check your password.'),
      );
    }
  };

  const handleClearAIMemory = async () => {
    if (
      !window.confirm(
        t(
          'profile.clearMemoryConfirm',
          'This will clear all AI conversation memory. Are you sure?',
        ),
      )
    ) {
      return;
    }

    try {
      await axios.post('/chat/clear-memory');
      alert(t('profile.memoryCleared', 'AI memory has been cleared successfully.'));
    } catch (error: any) {
      alert(t('profile.clearMemoryError', 'Failed to clear AI memory. Please try again.'));
    }
  };

  const handleExportData = async () => {
    try {
      const response = await axios.get('/users/export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chatnexus-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      alert(t('profile.exportDataError', 'Failed to export data. Please try again.'));
    }
  };

  const sections: SettingsSection[] = [
    {
      icon: UserIcon,
      title: t('profile.sections.profile', 'Profile'),
      items: [
        {
          label: t('profile.username', 'Username'),
          value: profile?.username || profile?.name || t('profile.defaultUser', 'User'),
          action: () => navigate('/settings/username'),
          showChevron: true,
        },
        {
          label: t('profile.genderAndOrientation', 'Gender & Sexual Orientation'),
          action: () => navigate('/settings/preferences'),
          showChevron: true,
        },
      ],
    },
    {
      icon: StarIcon,
      title: t('profile.sections.creditsSubscription', 'Credits & Subscription'),
      items: [
        {
          label: t('profile.creditsLabel', 'Credits'),
          value: `⭐ ${tokenBalance}`,
          action: () => navigate('/credits'),
          showChevron: true,
        },
        {
          label:
            profile?.subscriptionTier === 'premium'
              ? t('profile.companionPlusActive', 'Companion+ Active')
              : t('profile.upgradePlan', 'Upgrade Plan'),
          action: () => navigate('/subscription'),
          showChevron: true,
          color: 'text-primary-500',
        },
      ],
    },
    {
      icon: MoonIcon,
      title: t('profile.sections.appearance', 'Appearance'),
      items: [
        {
          label: t('profile.theme', 'Theme'),
          value: t(`profile.themes.${theme}`, theme.charAt(0).toUpperCase() + theme.slice(1)),
          action: () => navigate('/settings/theme'),
          showChevron: true,
        },
      ],
    },
    {
      icon: GlobeAltIcon,
      title: t('profile.sections.communication', 'Communication Preferences'),
      items: [
        {
          label: t('profile.language', 'Language'),
          value: getLanguageDisplay(),
          action: () => navigate('/settings/language'),
          showChevron: true,
        },
        {
          label: t('profile.phoneNumber', 'Phone Number & SMS'),
          value: profile?.phoneNumber
            ? `${profile.phoneNumber}${profile.smsConsentGiven ? ' ✓' : ''}`
            : t('profile.noPhone', 'Not set'),
          action: () => navigate('/settings/phone'),
          showChevron: true,
        },
        {
          label: t('profile.notifications', 'Notifications'),
          action: () => navigate('/settings/notifications'),
          showChevron: true,
        },
      ],
    },
    {
      icon: CreditCardIcon,
      title: t('profile.sections.account', 'Account'),
      items: [
        {
          label: t('profile.billingHistory', 'Billing History'),
          action: () => navigate('/billing'),
          showChevron: true,
        },
        {
          label: t('profile.manageSubscription', 'Manage Subscription'),
          action: () => navigate('/subscription'),
          showChevron: true,
        },
      ],
    },
    {
      icon: DocumentTextIcon,
      title: t('profile.sections.legalSupport', 'Legal & Support'),
      items: [
        {
          label: t('profile.support', 'Support'),
          value: '+1-415-663-6671',
          action: () => window.open('tel:+14156636671'),
          showChevron: false,
        },
        {
          label: t('profile.termsOfService', 'Terms of Service'),
          action: () => navigate('/terms'),
          showChevron: true,
        },
        {
          label: t('profile.privacyPolicy', 'Privacy Policy'),
          action: () => navigate('/privacy'),
          showChevron: true,
        },
        {
          label: t('profile.communityGuidelines', 'Community Guidelines'),
          action: () => navigate('/guidelines'),
          showChevron: true,
        },
      ],
    },
    {
      icon: ShieldCheckIcon,
      title: t('profile.sections.security', 'Security'),
      items: [
        ...((profile as any)?.hasPassword
          ? [
              {
                label: t('profile.changePassword', 'Change Password'),
                action: () => navigate('/settings/password'),
                showChevron: true,
              },
            ]
          : []),
        {
          label: t('profile.clearAIMemory', 'Clear AI Memory'),
          action: handleClearAIMemory,
          showChevron: false,
        },
        {
          label: t('profile.exportData', 'Export My Data'),
          action: handleExportData,
          showChevron: false,
        },
        {
          label: t('profile.signOut', 'Sign Out'),
          action: handleLogout,
          showChevron: false,
          color: 'text-red-400',
        },
        {
          label: t('profile.deleteAccount', 'Delete Account'),
          action: handleDeleteAccount,
          showChevron: false,
          color: 'text-red-500',
        },
      ],
    },
  ];

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-light-text-secondary dark:text-dark-text-secondary">
            {t('common.loading', 'Loading...')}
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-light-border dark:border-dark-border">
          <h1 className="text-lg font-semibold text-light-text dark:text-dark-text text-center">
            {t('profile.title', 'Profile & Settings')}
          </h1>
        </div>

        {/* Settings Sections */}
        <div className="flex-1 overflow-y-auto">
          {/* Account Info */}
          {profile && (
            <div className="px-4 py-3 border-b border-light-border dark:border-dark-border">
              <div className="text-center">
                <p className="text-base font-medium text-light-text dark:text-dark-text">
                  {profile.name || profile.username || t('profile.user', 'User')}
                </p>
                {profile.email && (
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-0.5">
                    {profile.email}
                  </p>
                )}
                {profile.phoneNumber && (
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-0.5">
                    {profile.phoneNumber}
                  </p>
                )}
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1 opacity-60">
                  {(profile as any).googleId
                    ? t('profile.signedInWith', 'Signed in with {{method}}', { method: 'Google' })
                    : (profile as any).appleId
                      ? t('profile.signedInWith', 'Signed in with {{method}}', { method: 'Apple' })
                      : profile.phoneNumber && !profile.email
                        ? t('profile.signedInWith', 'Signed in with {{method}}', {
                            method: 'Phone',
                          })
                        : t('profile.signedInWith', 'Signed in with {{method}}', {
                            method: 'Email',
                          })}
                </p>
              </div>
            </div>
          )}

          {/* Usage Dashboard */}
          <UsageDashboard />
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-2">
              {/* Section Header */}
              <div className="flex items-center gap-2 px-4 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
                <section.icon className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
                <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  {section.title}
                </span>
              </div>

              {/* Section Items */}
              <div className="bg-light-bg dark:bg-dark-bg">
                {section.items.map((item, itemIndex) => (
                  <button
                    key={itemIndex}
                    onClick={item.action}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-light-surface/30 dark:hover:bg-dark-surface/30 transition-colors border-b border-light-border dark:border-dark-border last:border-b-0"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span className={`text-light-text dark:text-dark-text ${item.color || ''}`}>
                        {item.label}
                      </span>
                      {item.value && (
                        <span className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
                          {item.value}
                        </span>
                      )}
                    </div>
                    {item.showChevron && (
                      <ChevronRightIcon className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* PWA Install Settings */}
          <div className="mb-2">
            <div className="flex items-center gap-2 px-4 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
              <DevicePhoneMobileIcon className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
              <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {t('profile.sections.app', 'App')}
              </span>
            </div>
            <div className="bg-light-bg dark:bg-dark-bg px-4">
              <InstallSettings />
            </div>
          </div>

          {/* Test Mode Indicator */}
          {profile?.isTestUser && (
            <div className="mx-4 my-6 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-500 text-sm text-center">
                🧪 {t('profile.testModeActive', 'Test Mode Active - Using Test Stripe Environment')}
              </p>
            </div>
          )}

          {/* Version */}
          <div className="py-8 text-center">
            <p className="text-light-text-secondary dark:text-dark-text-secondary text-xs">
              {t('profile.version', 'Version {{version}}', { version: '1.0.0' })}
            </p>
            <p className="text-light-text-secondary dark:text-dark-text-secondary text-xs mt-1">
              {t('profile.copyright', '© {{year}} ChatNexus', { year: 2025 })}
            </p>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Profile;
